import {
  ArrowPathIcon,
  ArrowLeftOnRectangleIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";

const LAYOUT_TOKENS = {
  shell:
    "min-h-screen bg-[radial-gradient(circle_at_top,_rgba(37,99,235,0.08),_transparent_42%),linear-gradient(180deg,#f8fafc_0%,#eef2ff_100%)] px-4 py-8 sm:px-6 lg:px-8",
  frame: "mx-auto grid max-w-6xl gap-6 lg:grid-cols-[1.1fr_0.9fr]",
  hero:
    "hidden rounded-[28px] border border-slate-200/80 bg-slate-950 p-10 text-white shadow-[0_28px_80px_-32px_rgba(15,23,42,0.72)] lg:flex lg:min-h-[580px] lg:flex-col lg:justify-between",
  card:
    "rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_24px_60px_-32px_rgba(15,23,42,0.28)] sm:p-8",
  badge:
    "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em]",
  primaryAction:
    "inline-flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto",
  secondaryAction:
    "inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2 sm:w-auto",
};

const TONE_TOKENS = {
  loading: {
    badge: "border-sky-200 bg-sky-50 text-sky-700",
    iconWrap: "bg-sky-100 text-sky-700",
    primary:
      "bg-sky-600 hover:bg-sky-500 focus:ring-sky-500",
    heroAccent: "bg-sky-500/12 text-sky-200 border-sky-400/20",
    eyebrow: "Sincronizando sessão",
    heroTitle: "Validando seu acesso ao painel",
    heroCopy:
      "Estamos confirmando a sessão ativa e o escopo operacional antes de liberar os módulos do WaiterCall.",
    points: [
      "Validação do contexto da empresa",
      "Reaplicação do restaurante selecionado",
      "Proteção contra redirecionamentos prematuros",
    ],
    icon: ArrowPathIcon,
    iconClass: "animate-spin",
  },
  recoverable: {
    badge: "border-amber-200 bg-amber-50 text-amber-700",
    iconWrap: "bg-amber-100 text-amber-700",
    primary:
      "bg-amber-600 hover:bg-amber-500 focus:ring-amber-500",
    heroAccent: "bg-amber-500/12 text-amber-200 border-amber-400/20",
    eyebrow: "Verificação temporariamente indisponível",
    heroTitle: "Sua sessão não pôde ser validada agora",
    heroCopy:
      "O painel preservou o contexto local, mas a confirmação com a API falhou. Revise a conectividade e tente novamente.",
    points: [
      "Falhas transitórias não derrubam a sessão automaticamente",
      "O retry reutiliza a mesma credencial ativa",
      "A limpeza manual continua disponível como ação secundária",
    ],
    icon: ExclamationTriangleIcon,
    iconClass: "",
  },
  blocked: {
    badge: "border-rose-200 bg-rose-50 text-rose-700",
    iconWrap: "bg-rose-100 text-rose-700",
    primary:
      "bg-rose-600 hover:bg-rose-500 focus:ring-rose-500",
    heroAccent: "bg-rose-500/12 text-rose-200 border-rose-400/20",
    eyebrow: "Acesso comercial bloqueado",
    heroTitle: "A assinatura precisa de regularização",
    heroCopy:
      "O acesso operacional foi interrompido pelo backend por bloqueio comercial. O estado abaixo mantém clareza sem invalidar a sessão indevidamente.",
    points: [
      "O usuário permanece identificado enquanto o bloqueio existir",
      "A tentativa de atualização consulta novamente o backend",
      "O logout continua disponível para troca de conta",
    ],
    icon: ShieldExclamationIcon,
    iconClass: "",
  },
};

export default function SessionStateScreen({
  variant = "loading",
  title,
  message,
  primaryAction,
  secondaryAction,
}) {
  const tone = TONE_TOKENS[variant] || TONE_TOKENS.loading;
  const Icon = tone.icon;

  return (
    <div className={LAYOUT_TOKENS.shell}>
      <div className={LAYOUT_TOKENS.frame}>
        <section className={LAYOUT_TOKENS.hero}>
          <div className="space-y-6">
            <span className={clsx(LAYOUT_TOKENS.badge, tone.heroAccent)}>
              Orion WaiterCall
            </span>
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-300">
                {tone.eyebrow}
              </p>
              <h1 className="text-4xl font-semibold tracking-tight text-white">
                {tone.heroTitle}
              </h1>
              <p className="max-w-xl text-base leading-7 text-slate-300">
                {tone.heroCopy}
              </p>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-300">
              Decisões desta camada
            </p>
            <ul className="mt-4 space-y-3 text-sm leading-6 text-slate-200">
              {tone.points.map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className={LAYOUT_TOKENS.card}>
          <div className="flex flex-col gap-6">
            <div className="flex items-start gap-4">
              <div
                className={clsx(
                  "flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl",
                  tone.iconWrap,
                )}
              >
                <Icon className={clsx("h-7 w-7", tone.iconClass)} />
              </div>
              <div className="min-w-0 space-y-2">
                <span className={clsx(LAYOUT_TOKENS.badge, tone.badge)}>
                  Estado de sessão
                </span>
                <h2 className="text-2xl font-semibold tracking-tight text-slate-950">
                  {title}
                </h2>
                <p className="text-sm leading-6 text-slate-600">{message}</p>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-slate-50/70 p-5">
              <p className="text-sm font-semibold text-slate-900">
                O que acontece agora
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                O painel pausou a navegação principal para evitar redirecionamento
                opaco ou logout indevido. A próxima ação depende do estado atual.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              {primaryAction ? (
                <button
                  type="button"
                  onClick={primaryAction.onClick}
                  disabled={primaryAction.disabled}
                  className={clsx(
                    LAYOUT_TOKENS.primaryAction,
                    tone.primary,
                    primaryAction.disabled && "cursor-not-allowed opacity-60",
                  )}
                >
                  {primaryAction.icon ?? null}
                  {primaryAction.label}
                </button>
              ) : null}

              {secondaryAction ? (
                <button
                  type="button"
                  onClick={secondaryAction.onClick}
                  disabled={secondaryAction.disabled}
                  className={clsx(
                    LAYOUT_TOKENS.secondaryAction,
                    secondaryAction.disabled && "cursor-not-allowed opacity-60",
                  )}
                >
                  {secondaryAction.icon ?? <ArrowLeftOnRectangleIcon className="h-4 w-4" />}
                  {secondaryAction.label}
                </button>
              ) : null}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
